import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css'; 

import cloudUpload from "../../assets/cloud-upload.svg";
import driveUpload from "../../assets/Integrations.svg";
import dropboxUpload from "../../assets/dropbox.svg";
import downloadIcon from "../../assets/Icon.png";
import menu from "../../assets/horizontalDot.png";
import doc from "../../assets/doc.png";
import { Link } from "react-router-dom";

const VacancyApplicationSchema = Yup.object().shape({
  fullName: Yup.string()
    .trim()
    .required("Full Name is required")
    .min(2, "Full Name must be at least 2 characters"),
  lastName: Yup.string()
    .trim()
    .required("Last Name is required")
    .min(2, "Last Name must be at least 2 characters"),
  pronous: Yup.string(),
  emailAddress: Yup.string()
    .email("Invalid email address")
    .required("Email Address is required"),
  phone: Yup.string().required("Phone number is required"),
  resume: Yup.mixed()
    .required("Resume is required")
    .test(
      "fileSize",
      "File size is too large (max 5MB)",
      (value) => value && value.size <= 10 * 1024 * 1024
    )
    .test(
      "fileType",
      "Unsupported file format",
      (value) => 
        value && 
        ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].includes(value.type)
    ),
  linkedin: Yup.string().url("Invalid LinkedIn URL"),
  hasWorkedBefore: Yup.string().required("Please select an option"),
  additionalInfo: Yup.string().max(240, "Maximum 240 characters allowed"),
  termsConfirmation: Yup.array().min(3, "Please confirm all terms")
});

const VacancyApplicationForm = () => {
  const type = ["Full-time", "Remote"];


  return (
    <div className="font-sans flex justify-center p-4 lg:p-10">
      <div className="max-w-1200 w-full flex flex-col lg:flex-row justify-between overflow-hidden px-1 sm:px-2">
        <div className="py-8 md:min-w-[446px]">
          <div className="w-full flex flex-col gap-4 sm:gap-6">
            <h1 className="text-xl sm:text-[40px] sm:leading-[48px] font-bold text-formLabel">
              Apply for this job
            </h1>
            <h3 className="text-[28px] font-bold text-formLabel">
              Risk Strategy Analyst
            </h3>
            <div className="flex gap-3">
              {type.map((item, index) => (
                <div 
                  key={index} 
                  className="border border-darkPurple text-darkPurple rounded-lg px-3 py-1"
                >
                  {item}
                </div>
              ))}
            </div>
            <div className="text-sm leading-7 font-semibold border-2 border-darkPurple text-darkPurple rounded-[40px] px-3 py-3 text-center w-full sm:max-w-[232px] mt-4 md:pt-2">
              Back to Vacancy
            </div>
          </div>
        </div>

        <div className="bg-white shadow-[0px_8px_24px_-3px_#1018281A,0px_8px_24px_-3px_#1018280D] rounded-3xl py-6 px-4 sm:px-6 lg:w-[693px] my-10 sm:my-5 overflow-auto">
          <Formik
            initialValues={{
              fullName: "",
              lastName: "",
              pronous: "",
              emailAddress: "",
              phone: "",
              resume: null,
              linkedin: "",
              hasWorkedBefore: "",
              additionalInfo: "",
              termsConfirmation: []
            }}
            validationSchema={VacancyApplicationSchema}
            onSubmit={(values, { setSubmitting }) => {
              const formData = new FormData();
              Object.keys(values).forEach(key => {
                if (key === 'resume') {
                  formData.append(key, values[key]);
                } else {
                  formData.append(key, values[key]);
                }
              });

              setTimeout(() => {
                alert("Application submitted successfully!");
                setSubmitting(false);
              }, 400);
            }}
          >
            {({ 
              setFieldValue, 
              values, 
              errors, 
              touched,
              isSubmitting 
            }) => (
              <Form className="flex flex-col gap-8">
                <div>
                  <label 
                    htmlFor="fullName" 
                    className="flex items-baseline justify-start text-formLabel font-medium text-sm mb-1"
                  >
                    Full Name
                    <span className="ml-1 text-red-500 text-lg">*</span>
                  </label>
                  <Field
                    type="text"
                    id="fullName"
                    name="fullName"
                    placeholder="Full Name"
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 text-sm ${
                      touched.fullName && errors.fullName 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:ring-primary'
                    }`}
                  />
                  <ErrorMessage 
                    name="fullName" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div>
                  <label 
                    htmlFor="lastName" 
                    className="block text-formLabel font-medium text-sm mb-1"
                  >
                    Last Name
                    <span className="ml-1 text-red-500 text-lg">*</span>
                  </label>
                  <Field
                    type="text"
                    id="lastName"
                    name="lastName"
                    placeholder="Last Name"
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 text-sm ${
                      touched.lastName && errors.lastName 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:ring-primary'
                    }`}
                  />
                  <ErrorMessage 
                    name="lastName" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div>
                  <label 
                    htmlFor="pronous" 
                    className="block text-formLabel font-medium text-sm mb-1"
                  >
                    Pronouns
                  </label>
                  <Field
                    as="select"
                    id="pronous"
                    name="pronous"
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-200 rounded-md appearance-none focus:outline-none focus:ring-1 focus:ring-primary bg-white text-sm"
                  >
                    <option value="">Select Pronouns</option>
                    <option value="he/him">He/Him</option>
                    <option value="she/her">She/Her</option>
                    <option value="they/them">They/Them</option>
                    <option value="other">Other</option>
                  </Field>
                </div>

                <div>
                  <label 
                    htmlFor="emailAddress" 
                    className="block text-formLabel font-medium text-sm mb-1"
                  >
                    Email Address
                    <span className="ml-1 text-red-500 text-lg">*</span>
                  </label>
                  <Field
                    type="email"
                    id="emailAddress"
                    name="emailAddress"
                    placeholder="Work Email"
                    className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 text-sm ${
                      touched.emailAddress && errors.emailAddress 
                        ? 'border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:ring-primary'
                    }`}
                  />
                  <ErrorMessage 
                    name="emailAddress" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div>
                <label 
                    htmlFor="phone"
                    className="block text-formLabel font-medium text-sm mb-1"
                >
                    Phone Number
                    <span className="ml-1 text-red-500 text-lg">*</span>
                </label>
                <PhoneInput
                    defaultCountry="us"
                    value={values.phone}
                    onChange={(phone) => setFieldValue('phone', phone)}
                    inputClassName={`w-full !h-11 px-3 !py-3 border !bg-[#FFF] ${
                    errors.phone 
                        ? 'border-red-500' 
                        : '!border-[#E5E7EB]'
                    } !rounded-r-lg`}
                    countrySelectorStyleProps={{
                    buttonClassName: '!px-3 py-2 border !rounded-l-lg !h-11 !bg-[#F3F4F6]',
                    dropdownStyleProps: {
                        className: 'absolute z-10 bg-white border rounded shadow-lg'
                    }
                    }}
                    style={{
                    '--react-international-phone-flag-width': '32px',
                    '--react-international-phone-flag-height': '24px'
                    }}
                />
                <ErrorMessage
                    name="phone"
                    component="div"
                    className="text-red-500 text-xs mt-1"
                />
                </div>
                    
                <div>
                  <label 
                    htmlFor="resume" 
                    className="block text-sm font-medium text-formLabel mb-2"
                  >
                    Resume/CV
                    <span className="ml-1 text-red-500 text-lg">*</span>
                  </label>
                  <input
                    id="resume"
                    name="resume"
                    type="file"
                    onChange={(event) => {
                      setFieldValue("resume", event.currentTarget.files[0]);
                    }}
                    className="hidden"
                  />
                  {!values.resume && <><div className="mt-1 flex justify-center px-3 sm:px-6 pt-5 pb-6 border border-gray-300 rounded-md mb-2">
                    <div className="space-y-1 flex flex-col items-center">
                      <img
                        src={cloudUpload}
                        alt="upload"
                        width={42}
                        height={42}
                        className="mb-2"
                      />
                      <div className="flex text-base test-darkGray font-medium">
                        <label
                          htmlFor="resume"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-darkPurple hover:text-darkPurple/80"
                        >
                          <span>Upload a file</span>
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </div>
                  <div className="mt-1 flex justify-center px-3 sm:px-6 pt-5 pb-6 border border-gray-300 rounded-md mb-2">
                    <div className="space-y-1 flex flex-col items-center">
                      <img
                        src={dropboxUpload}
                        alt="upload"
                        width={42}
                        height={42}
                        className="mb-2"
                      />
                      <div className="flex text-base test-darkGray font-medium">
                        <label
                          htmlFor="resume"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-darkPurple hover:text-darkPurple/80"
                        >
                          <span>Upload a file</span>
                        </label>
                        <p className="pl-1">from Dropbox</p>
                      </div>
                      <p className="text-gray-500">PNG, JPG, GIF up to 5MB</p>
                      {values.resume && (
                        <p className="text-sm text-green-600">
                          {values.resume.name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="mt-1 flex justify-center px-3 sm:px-6 pt-5 pb-6 border border-gray-300 rounded-md mb-2">
                    <div className="space-y-1 flex flex-col items-center">
                      <img
                        src={driveUpload}
                        alt="upload"
                        width={42}
                        height={42}
                        className="mb-2"
                      />
                      <div className="flex text-base test-darkGray font-medium">
                        <label
                          htmlFor="resume"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-darkPurple hover:text-darkPurple/80"
                        >
                          <span>Upload a file</span>
                        </label>
                        <p className="pl-1">from GoogleDrive</p>
                      </div>
                      <p className="text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </div>
                  </>}
                  {values.resume && 
                  <div className="flex items-center justify-between px-3 py-4 bg-[#F9FAFB] border border-[#F3F4F6] rounded-lg">
                    <div className="flex items-center gap-3">
                        <img src={doc} alt="pdf" width={28} height={32}/>
                        <div className="flex flex-col gap-2">
                            <label className="font-medium">{values.resume.name}</label>
                            <p className="text-sm text-[#6B7280]">{`525KB `} * {`100% uploaded`}</p>
                        </div>
                    </div>
                    <div className="flex gap-1 items-center justify-center">
                        <button><img src={downloadIcon} alt="download" width={24} height={24} className="min-w-6" /></button>
                        <button><img src={menu} alt="menu" width={24} height={24} className="min-w-6" /></button>
                    </div>
                  </div>}
                  <ErrorMessage 
                    name="resume" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div>
                  <label 
                    htmlFor="linkedin" 
                    className="block text-formLabel font-medium text-sm mb-1"
                  >
                    LinkedIn Profile
                  </label>
                  <div className="w-full flex border focus-within:outline-none focus-within:ring-1 focus-within:ring-offset-0 focus-within:ring-darkPurple rounded-lg">
                    <span className="border border-[#D1D5DB] rounded-e-none bg-[#F9FAFB] px-3 py-2 rounded-lg text-sm text-[#4B5563]">
                      https://
                    </span>
                    <Field
                      type="text"
                      name="linkedin"
                      className={`w-full ml-1 px-2 focus-within:outline-none focus-within:ring-1 focus-within:ring-offset-1 focus-within:ring-transparent rounded-lg ${
                        touched.linkedin && errors.linkedin 
                          ? 'border-red-500' 
                          : ''
                      }`}
                      placeholder="linkedin.com/in/yourprofile"
                    />
                  </div>
                  <ErrorMessage 
                    name="linkedin" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div>
                  <label 
                    htmlFor="hasWorkedBefore" 
                    className="block text-formLabel font-medium text-sm mb-2"
                  >
                    Have you ever worked for RegTechFlow before?
                  </label>
                  <div className="w-full flex flex-col gap-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Field
                        type="radio"
                        id="workedYes"
                        name="hasWorkedBefore"
                        value="yes"
                        className="cursor-pointer w-[24px] h-[24px] accent-darkPurple"
                      />
                      <label 
                        htmlFor="workedYes" 
                        className="cursor-pointer text-base"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Field
                        type="radio"
                        id="workedNo"
                        name="hasWorkedBefore"
                        value="no"
                        className="cursor-pointer w-[24px] h-[24px] accent-darkPurple"
                      />
                      <label 
                        htmlFor="workedNo" 
                        className="cursor-pointer text-base"
                      >
                        No
                      </label>
                    </div>
                  </div>
                  <ErrorMessage 
                    name="hasWorkedBefore" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div className="relative">
                  <label 
                    htmlFor="additionalInfo" 
                    className="block text-sm font-medium sm:mt-4 text-darkGray mb-2"
                  >
                    Additional Information (Optional)
                  </label>
                  <div className={`relative w-full px-3 sm:px-4 py-2.5 sm:py-3 border rounded-md focus:outline-none focus:ring-1 resize-none text-sm ${
                        touched.additionalInfo && errors.additionalInfo 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-gray-200 focus:ring-primary'
                      }`}>
                    <Field
                      as="textarea"
                      id="additionalInfo"
                      name="additionalInfo"
                      placeholder="Add a cover letter or anything else you want to share"
                      rows={4}
                      maxLength={240}
                      className={`w-[80%] md:w-[90%] shadow-none border-none outline-0 resize-none`}
                    />
                    <div className="flex justify-end absolute top-2 right-3">
                      <span className="text-sm text-[#6B7280]">
                        {values.additionalInfo ? values.additionalInfo.length : 0}/240
                      </span>
                    </div>
                  </div>
                  <ErrorMessage 
                    name="additionalInfo" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex gap-2.5 items-center">
                    <Field
                      type="checkbox"
                      id="termsConfirmation1"
                      name="termsConfirmation"
                      value="information_true"
                      className="accent-darkPurple"
                      style={{width:"24px", height:"24px", minWidth:"24px"}}
                    />
                    <label 
                      htmlFor="termsConfirmation1" 
                      className="text-sm text-[#6B7280] sm:text-[#6B7280]/80 font-medium"
                    >
                      By submitting this application, I confirm that all the information given by me in this application for employment and any additional documents attached hereto are true to the best of my knowledge and that I have not wilfully suppressed any material fact.
                    </label>
                  </div>
                  <div className="flex gap-2.5 items-center">
                    <Field
                      type="checkbox"
                      id="termsConfirmation2"
                      name="termsConfirmation"
                      value="previous_employment"
                      className="accent-darkPurple"
                      style={{width:"24px", height:"24px", minWidth:"24px"}}
                    />
                    <label 
                      htmlFor="termsConfirmation2" 
                      className="text-sm text-[#6B7280] sm:text-[#6B7280]/80 font-medium"
                    >
                      I confirm I have disclosed if applicable any previous employment with RegTechFlow.
                    </label>
                  </div>
                  <div className="flex gap-2.5 items-center">
                    <Field
                      type="checkbox"
                      id="termsConfirmation3"
                      name="termsConfirmation"
                      value="application_consequences"
                      className="accent-darkPurple"
                      style={{width:"24px", height:"24px", minWidth:"24px"}}
                    />
                    <label 
                      htmlFor="termsConfirmation3" 
                      className="text-sm text-[#6B7280] sm:text-[#6B7280]/80 font-medium"
                    >
                      I accept that if any of the information given by me in this application is in any way false or incorrect, my application may be rejected, any offer of employment may be withdrawn or my employment with RegTechFlow may be terminated summarily or I may be dismissed.
                    </label>
                  </div>
                  <ErrorMessage 
                    name="termsConfirmation" 
                    component="div" 
                    className="text-red-500 text-xs mt-1" 
                  />
                  <span className="text-sm text-[#6B7280]">For information on how we will handle your personal data when you apply for a job with us, please see our <Link to="#" className="text-darkPurple">Data Privacy Notice for Candidates.</Link></span>
                </div>

                <div className="pt-2 sm:pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-white border border-primary text-primary rounded-full hover:bg-primary hover:text-white transition-colors duration-200 font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default VacancyApplicationForm;